import { Button, Modal } from '@yoast/ui-library';
import React from 'react';

import { LockOpenIcon } from '@heroicons/react/outline';
import cx from 'classnames';
import InsightsSpiralIcon from '../assets/icon-insights-spiral.svg?react';
import SparklesGradientIcon from '../assets/icon-sparkles-gradient.svg?react';
import upsellImage2x from '../assets/upsell-demo@2x.png';
import { useBrandAnalysisData } from '../contexts';

/**
 * Props for ModalUpgrade.
 * @property isOpen - Controls whether the modal is visible.
 * @property variant - Determines the modal's behavior and messaging.
 *   Use 'purchase' to show upsell messaging and unlock button.
 *   Use 'trial' to show trial messaging, terms, and a "try for free" button.
 */
interface ModalUpgradeProps {
  isOpen: boolean;
  /**
   * Determines the modal's messaging and available actions.
   * - 'purchase': Shows upsell messaging and unlock button.
   * - 'trial': Shows trial messaging, terms, and a "try for free" button.
   */
  variant?: 'purchase' | 'trial';
  onClose?: () => void;
}

const ModalUpgrade: React.FC<ModalUpgradeProps> = ({
  isOpen,
  variant = 'purchase',
  onClose,
}) => {
  const {
    hostAppProps: { onAddSubscription },
  } = useBrandAnalysisData();

  const handleDiscoverNow = () => {
    // TODO: if onAddSubscription is not provided, we need to provide a
    // fallback, such as a link to Settings to disable another brand
    onAddSubscription?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose || (() => {})}
      position="center"
      as="div"
      className="[&_.yst-modal\_\_close-button]:yst-bg-transparent"
    >
      <Modal.Panel
        className={cx(
          'yst-relative yst-max-w-[512px] yst-rounded-3xl yst-bg-gradient-to-b yst-from-[rgba(166,30,105,0.25)] yst-via-white yst-via-40% yst-to-white yst-px-10',
          {
            'yst-pt-10': !!onClose,
            'yst-py-10': !onClose,
          },
        )}
        // don't allow close on purchase variant
        hasCloseButton={!!onClose}
      >
        <img
          src={upsellImage2x}
          alt="Upsell image"
          className="yst-mb-6 yst-rounded-lg yst-shadow-lg"
        />

        <Modal.Container.Header className="yst-mb-4 yst-space-y-4 yst-text-center">
          <p className="yst-flex yst-items-center yst-justify-center yst-gap-1 yst-text-center yst-text-xs yst-uppercase yst-text-slate-500">
            <InsightsSpiralIcon className="yst-h-6 yst-w-6" /> Yoast AI Brand
            Insights
          </p>
          {variant === 'trial' ? (
            <>
              <Modal.Title
                as="h2"
                className="yst-text-lg yst-font-medium yst-text-slate-900"
              >
                Your first brand analysis is free!
              </Modal.Title>
              <p className="yst-text-tiny yst-font-normal yst-text-slate-600">
                As a Yoast customer, you can run your first brand analysis at no
                cost. See how your brand shows up in AI responses.
              </p>
            </>
          ) : (
            <>
              <Modal.Title
                as="h2"
                className="yst-pr-8 yst-text-lg yst-font-medium yst-text-slate-900"
              >
                How does your brand show up in AI responses?
              </Modal.Title>
              <p className="yst-text-tiny yst-font-normal yst-text-slate-600">
                Track visibility, control perception, and stay ahead - tools to
                manage your AI presence are now live!
              </p>
            </>
          )}
        </Modal.Container.Header>

        <Modal.Container.Footer className="yst-mt-6 yst-flex yst-flex-col yst-gap-2">
          {variant === 'trial' ? (
            <Button
              onClick={onClose}
              className="yst-flex yst-w-full yst-items-center yst-gap-1 yst-border-transparent yst-bg-gradient-to-r yst-from-[#A61E69] yst-to-[#6366F1] yst-text-white yst-shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:yst-opacity-90 focus:yst-outline-[#A61E69]"
            >
              <SparklesGradientIcon className="yst-h-5 yst-w-5 [&_path]:yst-stroke-white" /> Set up free
              analysis
            </Button>
          ) : (
            <Button
              variant="upsell"
              onClick={handleDiscoverNow}
              className="yst-flex yst-w-full yst-items-center yst-gap-1"
            >
              <LockOpenIcon /> Unlock Yoast AI Brand Insights
            </Button>
          )}
          {!!onClose && (
            <Button
              onClick={onClose}
              variant="tertiary"
              className="yst-text-center"
            >
              Close
            </Button>
          )}
        </Modal.Container.Footer>
      </Modal.Panel>
    </Modal>
  );
};

export default ModalUpgrade;
